import { usePagesContent } from "../../Hooks/usePagesContent";
import {
    getIndicatorConfig,
    getIndicatorValuesFromRecord,
    isFieldIndicatorsEnabled,
    resolveIndicator,
} from "./indicatorRules";

/**
 * Resolves the marker to render for one field.
 *
 * Returns two things deliberately:
 *   active - the feature is on AND this field has a rule set. The field reserves its
 *            marker track whenever this is true, EVEN IF no rule matched this row, so
 *            a column's width does not depend on how many of its rows happen to carry
 *            a status. False means render nothing at all and leave layout untouched.
 *   marker - the matched rule, or null when this particular value has no marker.
 *
 * Reads the org flag from PagesContentContext itself so no caller has to check it -
 * the context defaults to null outside a provider, which reads as "feature off"
 * rather than throwing.
 *
 * `value`/`reason` win over `record`: table cells pass a row, while the Digital Twin
 * header has loose values off the entity-tags response and no row object at all.
 */
export const useFieldIndicator = ({ name, indicator, record, value, reason }) => {
    const pagesContent = usePagesContent();
    const metaInfo = pagesContent?.metaInfo;

    if (!isFieldIndicatorsEnabled(metaInfo)) return { active: false, marker: null };

    const indicatorConfig = indicator || getIndicatorConfig(name);
    if (!indicatorConfig) return { active: false, marker: null };

    const fromRecord = getIndicatorValuesFromRecord(record, indicatorConfig);
    const marker = resolveIndicator({
        indicator: indicatorConfig,
        value: value !== undefined ? value : fromRecord.value,
        reason: reason !== undefined ? reason : fromRecord.reason,
    });

    return {
        active: true,
        marker: marker ? { ...marker, placement: indicatorConfig.placement } : null,
    };
};

export default useFieldIndicator;
